import { EditScreenDto } from "src/coursepart/Lecture/domain/LectureState";
import { ScrapBookDb } from "./scrapBookDd";
import { ScrapLecture, ScrapScreen } from "./domain";
import api from "src/services/api/axiosService";
import { atom, getDefaultStore } from "jotai";
import { produce } from "immer";
import { LectureDtoWithOriginals } from "src/coursepart/domain/LectureDtoWithOriginals";


const endPoints = {

    GET_SCREENS: `/author/coursepart/screen/list`,
    GET_LECTURES: `/author/coursepart/lecture/list`,
}

const store = getDefaultStore();

const screensPrivate = atom<EditScreenDto[]>([]);
screensPrivate.debugLabel = "screensPrivate";
export const scrapScreensAtom = atom<EditScreenDto[]>((get) => get(screensPrivate));
scrapScreensAtom.debugLabel = "scrapScreensAtom";

const lecturesPrivate = atom<LectureDtoWithOriginals[]>([]);
lecturesPrivate.debugLabel = "lecturesPrivate";

export const scrapLecturesAtom = atom<LectureDtoWithOriginals[]>((get) => get(lecturesPrivate));
scrapLecturesAtom.debugLabel = "scrapLecturesAtom";

export class ScrapBookService {

    private db: ScrapBookDb;

    constructor() {
        this.db = new ScrapBookDb("scrapbook");
    }


    /* Clip book */
    public async getImageClip() {
        try {
            const clipboardItems = await navigator.clipboard.read();
            for (const clipboardItem of clipboardItems) {
                const type = clipboardItem.types.find(t => t.indexOf("image") > -1)
                if (type) {
                    const blob = await clipboardItem.getType(type);
                    const ext = type.split("/")?.[1] || "jpg";
                    return new File([blob], `clip.${ext}`, { type })
                }
            }
        } catch (err: any) {
            console.error(err.name, err.message);
        }
    }


    /* SCREENS */

    public async addScreens(screens: EditScreenDto[], partId: string) {


        const dataIdsAlreadyInScrapBook = (await this.db.screens
            .filter(sc => !!screens.find(s => s.ScreenId == sc.screenId))
            .toArray()).map(sc => sc.screenId)
            .reduce<Record<string, boolean>>((obj, item) => Object.assign(obj, { [item]: true }), {});

        // filter out screens that are already in the scrapbook
        screens = screens.filter(s => !dataIdsAlreadyInScrapBook[s.ScreenId]);

        // all screens already in scrap => do nothing
        if (screens.length === 0) return;

        const screensToAdd = screens.map(s => {
            return {
                lectureId: s.LectureId,
                partId: partId,
                screenId: s.ScreenId
            }
        });

        await this.db.screens.bulkAdd(screensToAdd);

        const screensInstore = store.get(screensPrivate);
        store.set(screensPrivate, [...screensInstore, ...screens]);
    }

    public async selectScreen(screenId: string) {
        const screens = store.get(screensPrivate);
        const newData = produce(screens, (draft) => {
            draft.forEach(s => s.Selected = s.ScreenId === screenId);
        });
        store.set(screensPrivate, newData)
    }

    public async removeScreen(screenId: string) {

        const dbData = await this.db.screens.get({ screenId: screenId });
        if (dbData && dbData.id) {
            await this.db.screens.delete(dbData.id!)
        }

        const screens = store.get(screensPrivate);
        store.set(screensPrivate, screens.filter(s => s.ScreenId !== screenId))
    }

    public async clearscreens() {

        await this.db.screens.clear()
        store.set(screensPrivate, [])
    }

    public async loadScreens() {

        const screenIds = (await this.db.screens.toArray()).map(sc => sc.screenId);
        const response = await api.post<EditScreenDto[]>(endPoints.GET_SCREENS, screenIds);

        if (response && response.status === 200) {
            store.set(screensPrivate, response.data);
        }

        return true;

    }

    /* LECTURES */

    public async addLectures(lectures: LectureDtoWithOriginals[]) {

        const dataIdsAlreadyInScrapBook = (await this.db.lectures
            .filter(sc => !!lectures.find(s => s.Id == sc.lectureId))
            .toArray()).map(sc => sc.lectureId)
            .reduce<Record<string, boolean>>((obj, item) => Object.assign(obj, { [item]: true }), {});

        // filter out lectures that are already in the scrapbook
        lectures = lectures.filter(s => !dataIdsAlreadyInScrapBook[s.Id]);


        // lectures already in scrap => do nothing
        if (lectures.length === 0) return;

        const lecturesToAdd = lectures.map(lect => {
            return {
                lectureId: lect.Id,
                partId: lect.PartId
            };
        });


        await this.db.lectures.bulkAdd(lecturesToAdd);

        const lecturesInDB = store.get(lecturesPrivate);
        store.set(lecturesPrivate, [...lecturesInDB, ...lectures]);

    }

    public async selectLecture(lectureId: string) {
        const lecture = store.get(lecturesPrivate);
        const newData = produce(lecture, (draft) => {
            draft.forEach(l => l.Selected = l.Id === lectureId);
        });
        store.set(lecturesPrivate, newData)
    }

    public async removeLecture(lectureId: string) {

        const dbData = await this.db.lectures.get({ lectureId: lectureId });
        if (dbData && dbData.id) {
            await this.db.lectures.delete(dbData.id!)
        }
        const lectures = store.get(lecturesPrivate);
        store.set(lecturesPrivate, lectures.filter(l => l.Id !== lectureId))
    }

    public async clearLectures() {

        await this.db.lectures.clear()
        store.set(lecturesPrivate, [])
    }

    public async loadLectures() {
        const lectureIds = (await this.db.lectures.toArray()).map(sc => sc.lectureId);
        const response = await api.post<LectureDtoWithOriginals[]>(endPoints.GET_LECTURES, lectureIds);
        if (response && response.status === 200) {
            store.set(lecturesPrivate, response.data);
        }

        return true;
    }

}

const scrapBookService = new ScrapBookService();

export default scrapBookService;